import { type ComponentType, type SVGProps } from 'react'

type AttentionTone = 'critical' | 'warn' | 'ready' | 'suggestion'

type Props = {
  label:       string
  Icon:        ComponentType<SVGProps<SVGSVGElement>>
  active:      boolean
  accentColor: string
  onClick:     () => void
  attention?:  AttentionTone | null
}

const ATTENTION_BG: Record<AttentionTone, string> = {
  critical:   '#C03030',
  warn:       '#D4A574',
  ready:      '#B5683A',
  suggestion: '#2B4A3A',
}

export function NavTab({ label, Icon, active, accentColor, onClick, attention }: Props) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className="relative flex flex-1 flex-col items-center gap-1 py-2 px-1"
    >
      <div className="relative">
        <Icon
          className="h-[22px] w-[22px] transition-colors duration-200"
          strokeWidth={active ? 1.9 : 1.6}
          stroke={active ? accentColor : '#9A8B73'}
          fill={active ? accentColor : 'none'}
          fillOpacity={active ? 0.12 : 0}
        />
        {attention && (
          <span
            className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full ring-[1.5px] ring-paper"
            style={{ backgroundColor: ATTENTION_BG[attention] }}
          />
        )}
      </div>

      <span
        className={
          active
            ? 'font-serif text-[11px] font-medium italic transition-colors duration-200'
            : 'text-[11px] font-medium transition-colors duration-200'
        }
        style={{ color: active ? accentColor : '#9A8B73' }}
      >
        {label}
      </span>

      {/* Bloom underline: 4px muted resting dot → 38px full pill when active */}
      <span
        aria-hidden
        className="absolute bottom-0 rounded-full transition-all duration-300 ease-out"
        style={{
          height:          '3px',
          width:           active ? '38px' : '4px',
          opacity:         active ? 1 : 0.35,
          backgroundColor: accentColor,
        }}
      />
    </button>
  )
}
